import {
  SETTINGS_ACTION_TYPES,
  SETTINGS_ACTION_FIELDS,
  appActionTypes,
  appToolFields,
} from './settings-actions-copy';
import { namespaceCopyFor } from '@/i18n/ns';
import appApprovalsCopy, { type AppApprovalsCopy } from '@/i18n/ns/app-approvals';
import { personalSettingsFields } from './personal-settings-copy';
import memberNotesCopy from '@/i18n/ns/meeting-member-notes';

export type ActionKind =
  | 'add_meeting_note'
  | 'create_skill_draft'
  | 'update_skill_draft'
  | 'create_agent'
  | 'update_my_settings'
  | 'save_memory'
  | 'forget_memory'
  | 'propose_company_knowledge'
  | 'suggest_page_edits'
  // Earlier chats' staged cards; labelled as suggest_page_edits (actionCopy).
  | 'propose_knowledge_change'
  | 'save_work_result';
export type ActionCopy = {
  approve: string;
  reject: string;
  open: string;
  hint: string;
  approvedByMessage: string;
  pending: string;
  running: string;
  approved: string;
  rejected: string;
  failed: string;
  expired: string;
  superseded: string;
  review: string;
  content: string;
  types: Record<ActionKind, string>;
  fields: Record<string, string>;
  /** Labels that belong to one tool's card, by tool name; they never enter the shared `fields`. */
  toolFields: Record<string, Record<string, string>>;
  /** The App cards' value words (On, Read only, Yes ...) in the same language. */
  words: AppApprovalsCopy;
};
type BaseActionCopy = Omit<ActionCopy, 'toolFields' | 'words'>;

/** This copy with one tool's own labels in front of the shared ones, for a card of that tool. */
export function copyForTool(copy: ActionCopy, tool: string): ActionCopy {
  const own = copy.toolFields?.[tool];
  return own ? { ...copy, fields: { ...copy.fields, ...own } } : copy;
}

const TYPE_KEYS: ActionKind[] = [
  'create_skill_draft',
  'update_skill_draft',
  'create_agent',
  'update_my_settings',
  'save_memory',
  'forget_memory',
  'propose_company_knowledge',
  'suggest_page_edits',
  'save_work_result',
];
const FIELD_KEYS = [
  'name',
  'title',
  'description',
  'instructions',
  'example',
  'text',
  'content',
  'statement',
  'scope',
  'theme',
  'language',
  'timezone',
  'chat_model_choice',
  'modelChoice',
  'fileIds',
  'sourceIds',
  'payload',
  'kind',
  'replaces',
  'baseRevision',
  'action',
  'category',
  'markdown',
  'reason',
  'evidence',
];
type Row = {
  controls: [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  types: string[];
  fields: string[];
};
const catalog = (row: Row): BaseActionCopy => {
  if (row.types.length !== TYPE_KEYS.length || row.fields.length !== FIELD_KEYS.length)
    throw new Error('incomplete action copy catalog');
  const [
    approve,
    reject,
    open,
    hint,
    approvedByMessage,
    pending,
    running,
    approved,
    rejected,
    failed,
    expired,
    superseded,
    review,
    content,
  ] = row.controls;
  return {
    approve,
    reject,
    open,
    hint,
    approvedByMessage,
    pending,
    running,
    approved,
    rejected,
    failed,
    expired,
    superseded,
    review,
    content,
    types: Object.fromEntries(TYPE_KEYS.map((key, index) => [key, row.types[index]])) as Record<
      ActionKind,
      string
    >,
    fields: Object.fromEntries(FIELD_KEYS.map((key, index) => [key, row.fields[index]])),
  };
};

const en = catalog({
  controls: [
    'Approve',
    'Reject',
    'Open',
    'You can also say “approve”.',
    'Approved by your message',
    'Pending',
    'Running',
    'Approved',
    'Rejected',
    'Failed',
    'Expired',
    'Superseded',
    'Review request',
    'Content',
  ],
  types: [
    'Create Skill draft',
    'Update Skill draft',
    'Create agent draft',
    'Change settings',
    'Save memory',
    'Forget memory',
    'Suggest company knowledge',
    'Suggest knowledge page change',
    'Save workspace result',
  ],
  fields: [
    'Name',
    'Title',
    'Description',
    'Instructions',
    'Example',
    'Text',
    'Content',
    'Statement',
    'Scope',
    'Theme',
    'Language',
    'Time zone',
    'Chat model',
    'Model',
    'Files',
    'Sources',
    'Content',
    'Type',
    'Replaces',
    'Base revision',
    'Action',
    'Category',
    'Content',
    'Reason',
    'Evidence',
  ],
});
const sv = catalog({
  controls: [
    'Godkänn',
    'Avvisa',
    'Öppna',
    'Du kan också skriva ”godkänn”.',
    'Godkänd av ditt meddelande',
    'Väntar på beslut',
    'Körs',
    'Godkänd',
    'Avvisad',
    'Misslyckad',
    'Utgången',
    'Ersatt',
    'Granska begäran',
    'Innehåll',
  ],
  types: [
    'Skapa Skill-utkast',
    'Uppdatera Skill-utkast',
    'Skapa agentutkast',
    'Ändra inställningar',
    'Spara minne',
    'Glöm minne',
    'Föreslå företagskunskap',
    'Föreslå kunskapssidändring',
    'Spara arbetsresultat',
  ],
  fields: [
    'Namn',
    'Titel',
    'Beskrivning',
    'Instruktioner',
    'Exempel',
    'Text',
    'Innehåll',
    'Påstående',
    'Omfattning',
    'Tema',
    'Språk',
    'Tidszon',
    'Chattmodell',
    'Modell',
    'Filer',
    'Källor',
    'Innehåll',
    'Typ',
    'Ersätter',
    'Basrevision',
    'Åtgärd',
    'Kategori',
    'Innehåll',
    'Orsak',
    'Bevis',
  ],
});
const da = catalog({
  controls: [
    'Godkend',
    'Afvis',
    'Åbn',
    'Du kan også skrive ”godkend”.',
    'Godkendt af din besked',
    'Afventer',
    'Kører',
    'Godkendt',
    'Afvist',
    'Mislykket',
    'Udløbet',
    'Erstattet',
    'Gennemgå anmodning',
    'Indhold',
  ],
  types: [
    'Opret Skill-kladde',
    'Opdater Skill-kladde',
    'Opret agentkladde',
    'Skift indstillinger',
    'Gem hukommelse',
    'Glem hukommelse',
    'Foreslå virksomhedsviden',
    'Foreslå videnssideændring',
    'Gem arbejdsresultat',
  ],
  fields: [
    'Navn',
    'Titel',
    'Beskrivelse',
    'Instruktioner',
    'Eksempel',
    'Tekst',
    'Indhold',
    'Udsagn',
    'Omfang',
    'Tema',
    'Sprog',
    'Tidszone',
    'Chatmodel',
    'Model',
    'Filer',
    'Kilder',
    'Indhold',
    'Type',
    'Erstatter',
    'Basisrevision',
    'Handling',
    'Kategori',
    'Indhold',
    'Årsag',
    'Dokumentation',
  ],
});
const de = catalog({
  controls: [
    'Genehmigen',
    'Ablehnen',
    'Öffnen',
    'Du kannst auch „genehmigen“ schreiben.',
    'Durch deine Nachricht genehmigt',
    'Ausstehend',
    'Wird ausgeführt',
    'Genehmigt',
    'Abgelehnt',
    'Fehlgeschlagen',
    'Abgelaufen',
    'Ersetzt',
    'Anfrage prüfen',
    'Inhalt',
  ],
  types: [
    'Skill-Entwurf erstellen',
    'Skill-Entwurf aktualisieren',
    'Agentenentwurf erstellen',
    'Einstellungen ändern',
    'Erinnerung speichern',
    'Erinnerung vergessen',
    'Unternehmenswissen vorschlagen',
    'Wissensseite vorschlagen',
    'Arbeitsergebnis speichern',
  ],
  fields: [
    'Name',
    'Titel',
    'Beschreibung',
    'Anweisungen',
    'Beispiel',
    'Text',
    'Inhalt',
    'Aussage',
    'Bereich',
    'Thema',
    'Sprache',
    'Zeitzone',
    'Chatmodell',
    'Modell',
    'Dateien',
    'Quellen',
    'Inhalt',
    'Typ',
    'Ersetzt',
    'Basisrevision',
    'Aktion',
    'Kategorie',
    'Inhalt',
    'Grund',
    'Belege',
  ],
});
const es = catalog({
  controls: [
    'Aprobar',
    'Rechazar',
    'Abrir',
    'También puedes decir «aprobar».',
    'Aprobado por tu mensaje',
    'Pendiente',
    'En curso',
    'Aprobado',
    'Rechazado',
    'Fallido',
    'Caducado',
    'Sustituido',
    'Revisar solicitud',
    'Contenido',
  ],
  types: [
    'Crear borrador de Skill',
    'Actualizar borrador de Skill',
    'Crear borrador de agente',
    'Cambiar configuración',
    'Guardar memoria',
    'Olvidar memoria',
    'Sugerir conocimiento de empresa',
    'Sugerir cambio de página',
    'Guardar resultado',
  ],
  fields: [
    'Nombre',
    'Título',
    'Descripción',
    'Instrucciones',
    'Ejemplo',
    'Texto',
    'Contenido',
    'Declaración',
    'Ámbito',
    'Tema',
    'Idioma',
    'Zona horaria',
    'Modelo de chat',
    'Modelo',
    'Archivos',
    'Fuentes',
    'Contenido',
    'Tipo',
    'Sustituye',
    'Revisión base',
    'Acción',
    'Categoría',
    'Contenido',
    'Motivo',
    'Evidencia',
  ],
});
const fr = catalog({
  controls: [
    'Approuver',
    'Refuser',
    'Ouvrir',
    'Vous pouvez aussi dire « approuver ».',
    'Approuvé par votre message',
    'En attente',
    'En cours',
    'Approuvé',
    'Refusé',
    'Échec',
    'Expiré',
    'Remplacé',
    'Examiner la demande',
    'Contenu',
  ],
  types: [
    'Créer un brouillon de Skill',
    'Mettre à jour le brouillon de Skill',
    'Créer un brouillon d’agent',
    'Modifier les réglages',
    'Enregistrer un souvenir',
    'Oublier un souvenir',
    'Suggérer une connaissance d’entreprise',
    'Suggérer une modification de page',
    'Enregistrer un résultat',
  ],
  fields: [
    'Nom',
    'Titre',
    'Description',
    'Instructions',
    'Exemple',
    'Texte',
    'Contenu',
    'Énoncé',
    'Portée',
    'Thème',
    'Langue',
    'Fuseau horaire',
    'Modèle de chat',
    'Modèle',
    'Fichiers',
    'Sources',
    'Contenu',
    'Type',
    'Remplace',
    'Révision de base',
    'Action',
    'Catégorie',
    'Contenu',
    'Motif',
    'Preuve',
  ],
});
const it = catalog({
  controls: [
    'Approva',
    'Rifiuta',
    'Apri',
    'Puoi anche dire «approva».',
    'Approvato dal tuo messaggio',
    'In attesa',
    'In esecuzione',
    'Approvato',
    'Rifiutato',
    'Fallito',
    'Scaduto',
    'Sostituito',
    'Esamina richiesta',
    'Contenuto',
  ],
  types: [
    'Crea bozza Skill',
    'Aggiorna bozza Skill',
    'Crea bozza agente',
    'Modifica impostazioni',
    'Salva memoria',
    'Dimentica memoria',
    'Suggerisci conoscenza aziendale',
    'Suggerisci modifica pagina',
    'Salva risultato',
  ],
  fields: [
    'Nome',
    'Titolo',
    'Descrizione',
    'Istruzioni',
    'Esempio',
    'Testo',
    'Contenuto',
    'Dichiarazione',
    'Ambito',
    'Tema',
    'Lingua',
    'Fuso orario',
    'Modello chat',
    'Modello',
    'File',
    'Fonti',
    'Contenuto',
    'Tipo',
    'Sostituisce',
    'Revisione base',
    'Azione',
    'Categoria',
    'Contenuto',
    'Motivo',
    'Prova',
  ],
});
const nl = catalog({
  controls: [
    'Goedkeuren',
    'Afwijzen',
    'Openen',
    'Je kunt ook “goedkeuren” zeggen.',
    'Goedgekeurd via je bericht',
    'In afwachting',
    'Wordt uitgevoerd',
    'Goedgekeurd',
    'Afgewezen',
    'Mislukt',
    'Verlopen',
    'Vervangen',
    'Verzoek bekijken',
    'Inhoud',
  ],
  types: [
    'Skillconcept maken',
    'Skillconcept bijwerken',
    'Agentconcept maken',
    'Instellingen wijzigen',
    'Geheugen opslaan',
    'Geheugen vergeten',
    'Bedrijfskennis voorstellen',
    'Kenniswijziging voorstellen',
    'Werkresultaat opslaan',
  ],
  fields: [
    'Naam',
    'Titel',
    'Beschrijving',
    'Instructies',
    'Voorbeeld',
    'Tekst',
    'Inhoud',
    'Stelling',
    'Bereik',
    'Thema',
    'Taal',
    'Tijdzone',
    'Chatmodel',
    'Model',
    'Bestanden',
    'Bronnen',
    'Inhoud',
    'Type',
    'Vervangt',
    'Basisrevisie',
    'Actie',
    'Categorie',
    'Inhoud',
    'Reden',
    'Bewijs',
  ],
});
const pt = catalog({
  controls: [
    'Aprovar',
    'Rejeitar',
    'Abrir',
    'Também pode dizer «aprovar».',
    'Aprovado pela sua mensagem',
    'Pendente',
    'Em execução',
    'Aprovado',
    'Rejeitado',
    'Falhou',
    'Expirado',
    'Substituído',
    'Rever pedido',
    'Conteúdo',
  ],
  types: [
    'Criar rascunho de Skill',
    'Atualizar rascunho de Skill',
    'Criar rascunho de agente',
    'Alterar definições',
    'Guardar memória',
    'Esquecer memória',
    'Sugerir conhecimento da empresa',
    'Sugerir alteração de página',
    'Guardar resultado',
  ],
  fields: [
    'Nome',
    'Título',
    'Descrição',
    'Instruções',
    'Exemplo',
    'Texto',
    'Conteúdo',
    'Afirmação',
    'Âmbito',
    'Tema',
    'Idioma',
    'Fuso horário',
    'Modelo de chat',
    'Modelo',
    'Ficheiros',
    'Fontes',
    'Conteúdo',
    'Tipo',
    'Substitui',
    'Revisão base',
    'Ação',
    'Categoria',
    'Conteúdo',
    'Motivo',
    'Evidência',
  ],
});
const pl = catalog({
  controls: [
    'Zatwierdź',
    'Odrzuć',
    'Otwórz',
    'Możesz też napisać „zatwierdź”.',
    'Zatwierdzone przez Twoją wiadomość',
    'Oczekuje',
    'W toku',
    'Zatwierdzone',
    'Odrzucone',
    'Nieudane',
    'Wygasłe',
    'Zastąpione',
    'Przejrzyj prośbę',
    'Treść',
  ],
  types: [
    'Utwórz szkic Skill',
    'Zaktualizuj szkic Skill',
    'Utwórz szkic agenta',
    'Zmień ustawienia',
    'Zapisz pamięć',
    'Zapomnij pamięć',
    'Zaproponuj wiedzę firmy',
    'Zaproponuj zmianę strony',
    'Zapisz wynik',
  ],
  fields: [
    'Nazwa',
    'Tytuł',
    'Opis',
    'Instrukcje',
    'Przykład',
    'Tekst',
    'Treść',
    'Stwierdzenie',
    'Zakres',
    'Motyw',
    'Język',
    'Strefa czasowa',
    'Model czatu',
    'Model',
    'Pliki',
    'Źródła',
    'Treść',
    'Typ',
    'Zastępuje',
    'Wersja bazowa',
    'Działanie',
    'Kategoria',
    'Treść',
    'Powód',
    'Dowody',
  ],
});
const other = (labels: Row['controls'], typeLabels: string[], fieldLabels: string[]) =>
  catalog({ controls: labels, types: typeLabels, fields: fieldLabels });
/* Coverage tables are intentionally explicit per locale below. */
const commonFields = [
  'Name',
  'Title',
  'Description',
  'Instructions',
  'Example',
  'Text',
  'Content',
  'Statement',
  'Scope',
  'Theme',
  'Language',
  'Time zone',
  'Chat model',
  'Model',
  'Files',
  'Sources',
  'Content',
  'Type',
  'Replaces',
  'Base revision',
  'Action',
  'Category',
  'Content',
  'Reason',
  'Evidence',
];
const commonTypes = [
  'Create Skill draft',
  'Update Skill draft',
  'Create agent draft',
  'Change settings',
  'Save memory',
  'Forget memory',
  'Suggest company knowledge',
  'Suggest knowledge page change',
  'Save workspace result',
];
export const ACTION_COPY: Record<string, BaseActionCopy> = {
  en,
  bg: other(
    [
      'Одобри',
      'Откажи',
      'Отвори',
      'Можете да кажете „одобри“.',
      'Одобрено с вашето съобщение',
      'Изчаква',
      'Изпълнява се',
      'Одобрено',
      'Отказано',
      'Неуспешно',
      'Изтекло',
      'Заменено',
      'Преглед на заявката',
      'Съдържание',
    ],
    [
      'Създай чернова на Skill',
      'Обнови чернова на Skill',
      'Създай чернова на агент',
      'Промени настройките',
      'Запази памет',
      'Забрави памет',
      'Предложи фирмено знание',
      'Предложи промяна на страница',
      'Запази резултат',
    ],
    [
      'Име',
      'Заглавие',
      'Описание',
      'Инструкции',
      'Пример',
      'Текст',
      'Съдържание',
      'Твърдение',
      'Обхват',
      'Тема',
      'Език',
      'Часова зона',
      'Чат модел',
      'Модел',
      'Файлове',
      'Източници',
      'Съдържание',
      'Тип',
      'Заменя',
      'Базова версия',
      'Действие',
      'Категория',
      'Съдържание',
      'Причина',
      'Доказателства',
    ],
  ),
  cs: other(
    [
      'Schválit',
      'Odmítnout',
      'Otevřít',
      'Můžete také říct „schválit“.',
      'Schváleno vaší zprávou',
      'Čeká',
      'Probíhá',
      'Schváleno',
      'Zamítnuto',
      'Selhalo',
      'Platnost vypršela',
      'Nahrazeno',
      'Zkontrolovat žádost',
      'Obsah',
    ],
    [
      'Vytvořit koncept Skill',
      'Aktualizovat koncept Skill',
      'Vytvořit koncept agenta',
      'Změnit nastavení',
      'Uložit vzpomínku',
      'Zapomenout vzpomínku',
      'Navrhnout firemní znalost',
      'Navrhnout změnu stránky',
      'Uložit výsledek',
    ],
    [
      'Název',
      'Titulek',
      'Popis',
      'Pokyny',
      'Příklad',
      'Text',
      'Obsah',
      'Tvrzení',
      'Rozsah',
      'Motiv',
      'Jazyk',
      'Časové pásmo',
      'Chatovací model',
      'Model',
      'Soubory',
      'Zdroje',
      'Obsah',
      'Typ',
      'Nahrazuje',
      'Základní revize',
      'Akce',
      'Kategorie',
      'Obsah',
      'Důvod',
      'Důkazy',
    ],
  ),
  el: other(
    [
      'Έγκριση',
      'Απόρριψη',
      'Άνοιγμα',
      'Μπορείτε επίσης να πείτε «έγκριση».',
      'Εγκρίθηκε με το μήνυμά σας',
      'Σε αναμονή',
      'Εκτελείται',
      'Εγκρίθηκε',
      'Απορρίφθηκε',
      'Απέτυχε',
      'Έληξε',
      'Αντικαταστάθηκε',
      'Έλεγχος αιτήματος',
      'Περιεχόμενο',
    ],
    [
      'Δημιουργία προσχεδίου Skill',
      'Ενημέρωση προσχεδίου Skill',
      'Δημιουργία προσχεδίου agent',
      'Αλλαγή ρυθμίσεων',
      'Αποθήκευση μνήμης',
      'Λήθη μνήμης',
      'Πρόταση εταιρικής γνώσης',
      'Πρόταση αλλαγής σελίδας γνώσης',
      'Αποθήκευση αποτελέσματος',
    ],
    [
      'Όνομα',
      'Τίτλος',
      'Περιγραφή',
      'Οδηγίες',
      'Παράδειγμα',
      'Κείμενο',
      'Περιεχόμενο',
      'Δήλωση',
      'Εμβέλεια',
      'Θέμα',
      'Γλώσσα',
      'Ζώνη ώρας',
      'Μοντέλο συνομιλίας',
      'Μοντέλο',
      'Αρχεία',
      'Πηγές',
      'Περιεχόμενο',
      'Τύπος',
      'Αντικαθιστά',
      'Βασική αναθεώρηση',
      'Ενέργεια',
      'Κατηγορία',
      'Περιεχόμενο',
      'Αιτία',
      'Τεκμήρια',
    ],
  ),
  et: other(
    [
      'Kinnita',
      'Keeldu',
      'Ava',
      'Võid ka öelda „kinnita”.',
      'Kinnitatud sinu sõnumiga',
      'Ootel',
      'Töös',
      'Kinnitatud',
      'Tagasi lükatud',
      'Ebaõnnestus',
      'Aegunud',
      'Asendatud',
      'Vaata taotlus üle',
      'Sisu',
    ],
    [
      'Loo Skilli mustand',
      'Uuenda Skilli mustandit',
      'Loo agendi mustand',
      'Muuda seadeid',
      'Salvesta mälu',
      'Unusta mälu',
      'Paku ettevõtte teadmisi',
      'Paku teadmiste lehe muudatust',
      'Salvesta tulemus',
    ],
    [
      'Nimi',
      'Pealkiri',
      'Kirjeldus',
      'Juhised',
      'Näide',
      'Tekst',
      'Sisu',
      'Väide',
      'Ulatus',
      'Teema',
      'Keel',
      'Ajavöönd',
      'Vestlusmudel',
      'Mudel',
      'Failid',
      'Allikad',
      'Sisu',
      'Tüüp',
      'Asendab',
      'Põhirevisjon',
      'Toiming',
      'Kategooria',
      'Sisu',
      'Põhjus',
      'Tõendid',
    ],
  ),
  fi: other(
    [
      'Hyväksy',
      'Hylkää',
      'Avaa',
      'Voit myös sanoa ”hyväksy”.',
      'Hyväksytty viestilläsi',
      'Odottaa',
      'Käynnissä',
      'Hyväksytty',
      'Hylätty',
      'Epäonnistui',
      'Vanhentunut',
      'Korvattu',
      'Tarkista pyyntö',
      'Sisältö',
    ],
    [
      'Luo Skill-luonnos',
      'Päivitä Skill-luonnos',
      'Luo agenttiluonnos',
      'Muuta asetuksia',
      'Tallenna muisti',
      'Unohda muisti',
      'Ehdota yritystietoa',
      'Ehdota tietosivun muutosta',
      'Tallenna tulos',
    ],
    [
      'Nimi',
      'Otsikko',
      'Kuvaus',
      'Ohjeet',
      'Esimerkki',
      'Teksti',
      'Sisältö',
      'Lausunto',
      'Laajuus',
      'Teema',
      'Kieli',
      'Aikavyöhyke',
      'Chat-malli',
      'Malli',
      'Tiedostot',
      'Lähteet',
      'Sisältö',
      'Tyyppi',
      'Korvaa',
      'Perusversio',
      'Toiminto',
      'Luokka',
      'Sisältö',
      'Syy',
      'Todisteet',
    ],
  ),
  ga: other(
    [
      'Ceadaigh',
      'Diúltaigh',
      'Oscail',
      'Is féidir leat “ceadaigh” a rá freisin.',
      'Ceadaithe le do theachtaireacht',
      'Ar feitheamh',
      'Ar siúl',
      'Ceadaithe',
      'Diúltaithe',
      'Theip',
      'Imithe in éag',
      'Curtha ina áit',
      'Déan athbhreithniú',
      'Ábhar',
    ],
    [
      'Dréacht Skill a chruthú',
      'Dréacht Skill a nuashonrú',
      'Dréacht gníomhaire a chruthú',
      'Socruithe a athrú',
      'Cuimhne a shábháil',
      'Cuimhne a ligean i ndearmad',
      'Eolas cuideachta a mholadh',
      'Athrú leathanach eolais a mholadh',
      'Toradh a shábháil',
    ],
    [
      'Ainm',
      'Teideal',
      'Cur síos',
      'Treoracha',
      'Sampla',
      'Téacs',
      'Ábhar',
      'Ráiteas',
      'Raon',
      'Téama',
      'Teanga',
      'Crios ama',
      'Samhail comhrá',
      'Samhail',
      'Comhaid',
      'Foinsí',
      'Ábhar',
      'Cineál',
      'Ionadaíonn',
      'Athbhreithniú bonn',
      'Gníomh',
      'Catagóir',
      'Ábhar',
      'Cúis',
      'Fianaise',
    ],
  ),
  hr: other(
    [
      'Odobri',
      'Odbij',
      'Otvori',
      'Možeš reći i „odobri”.',
      'Odobreno tvojom porukom',
      'Na čekanju',
      'U tijeku',
      'Odobreno',
      'Odbijeno',
      'Neuspješno',
      'Isteklo',
      'Zamijenjeno',
      'Pregledaj zahtjev',
      'Sadržaj',
    ],
    [
      'Izradi nacrt Skilla',
      'Ažuriraj nacrt Skilla',
      'Izradi nacrt agenta',
      'Promijeni postavke',
      'Spremi memoriju',
      'Zaboravi memoriju',
      'Predloži znanje tvrtke',
      'Predloži promjenu stranice',
      'Spremi rezultat',
    ],
    [
      'Naziv',
      'Naslov',
      'Opis',
      'Upute',
      'Primjer',
      'Tekst',
      'Sadržaj',
      'Tvrdnja',
      'Opseg',
      'Tema',
      'Jezik',
      'Vremenska zona',
      'Model chata',
      'Model',
      'Datoteke',
      'Izvori',
      'Sadržaj',
      'Vrsta',
      'Zamjenjuje',
      'Osnovna revizija',
      'Radnja',
      'Kategorija',
      'Sadržaj',
      'Razlog',
      'Dokazi',
    ],
  ),
  hu: other(
    [
      'Jóváhagyás',
      'Elutasítás',
      'Megnyitás',
      'Azt is mondhatod: „jóváhagyás”.',
      'Az üzeneteddel jóváhagyva',
      'Függőben',
      'Fut',
      'Jóváhagyva',
      'Elutasítva',
      'Sikertelen',
      'Lejárt',
      'Felülírva',
      'Kérés áttekintése',
      'Tartalom',
    ],
    [
      'Skill-vázlat létrehozása',
      'Skill-vázlat frissítése',
      'Ügynökvázlat létrehozása',
      'Beállítások módosítása',
      'Emlék mentése',
      'Emlék elfelejtése',
      'Céges tudás javaslata',
      'Tudásoldal módosításának javaslata',
      'Eredmény mentése',
    ],
    [
      'Név',
      'Cím',
      'Leírás',
      'Utasítások',
      'Példa',
      'Szöveg',
      'Tartalom',
      'Állítás',
      'Hatókör',
      'Téma',
      'Nyelv',
      'Időzóna',
      'Csevegési modell',
      'Modell',
      'Fájlok',
      'Források',
      'Tartalom',
      'Típus',
      'Helyettesíti',
      'Alapverzió',
      'Művelet',
      'Kategória',
      'Tartalom',
      'Ok',
      'Bizonyíték',
    ],
  ),
  lt: other(
    [
      'Patvirtinti',
      'Atmesti',
      'Atidaryti',
      'Taip pat galite pasakyti „patvirtinti“.',
      'Patvirtinta jūsų žinute',
      'Laukiama',
      'Vykdoma',
      'Patvirtinta',
      'Atmesta',
      'Nepavyko',
      'Baigėsi galiojimas',
      'Pakeista',
      'Peržiūrėti užklausą',
      'Turinys',
    ],
    [
      'Sukurti Skill juodraštį',
      'Atnaujinti Skill juodraštį',
      'Sukurti agento juodraštį',
      'Keisti nustatymus',
      'Išsaugoti atmintį',
      'Pamiršti atmintį',
      'Siūlyti įmonės žinias',
      'Siūlyti žinių puslapio pakeitimą',
      'Išsaugoti rezultatą',
    ],
    [
      'Pavadinimas',
      'Antraštė',
      'Aprašas',
      'Instrukcijos',
      'Pavyzdys',
      'Tekstas',
      'Turinys',
      'Teiginys',
      'Aprėptis',
      'Tema',
      'Kalba',
      'Laiko juosta',
      'Pokalbio modelis',
      'Modelis',
      'Failai',
      'Šaltiniai',
      'Turinys',
      'Tipas',
      'Pakeičia',
      'Bazinė redakcija',
      'Veiksmas',
      'Kategorija',
      'Turinys',
      'Priežastis',
      'Įrodymai',
    ],
  ),
  lv: other(
    [
      'Apstiprināt',
      'Noraidīt',
      'Atvērt',
      'Varat arī teikt “apstiprināt”.',
      'Apstiprināts ar jūsu ziņu',
      'Gaida',
      'Tiek izpildīts',
      'Apstiprināts',
      'Noraidīts',
      'Neizdevās',
      'Beidzies',
      'Aizstāts',
      'Pārskatīt pieprasījumu',
      'Saturs',
    ],
    [
      'Izveidot Skill melnrakstu',
      'Atjaunināt Skill melnrakstu',
      'Izveidot aģenta melnrakstu',
      'Mainīt iestatījumus',
      'Saglabāt atmiņu',
      'Aizmirst atmiņu',
      'Ieteikt uzņēmuma zināšanas',
      'Ieteikt zināšanu lapas izmaiņu',
      'Saglabāt rezultātu',
    ],
    [
      'Nosaukums',
      'Virsraksts',
      'Apraksts',
      'Norādījumi',
      'Piemērs',
      'Teksts',
      'Saturs',
      'Apgalvojums',
      'Tvērums',
      'Motīvs',
      'Valoda',
      'Laika josla',
      'Tērzēšanas modelis',
      'Modelis',
      'Faili',
      'Avoti',
      'Saturs',
      'Tips',
      'Aizstāj',
      'Pamata redakcija',
      'Darbība',
      'Kategorija',
      'Saturs',
      'Iemesls',
      'Pierādījumi',
    ],
  ),
  mt: other(
    [
      'Approva',
      'Irrifjuta',
      'Iftaħ',
      'Tista’ tgħid ukoll “approva”.',
      'Approvat bil-messaġġ tiegħek',
      'Pendenti',
      'Għaddej',
      'Approvat',
      'Irrifjutat',
      'Falla',
      'Skada',
      'Sostitwit',
      'Irrevedi t-talba',
      'Kontenut',
    ],
    [
      'Oħloq abbozz ta’ Skill',
      'Aġġorna abbozz ta’ Skill',
      'Oħloq abbozz ta’ aġent',
      'Ibdel is-settings',
      'Issejvja memorja',
      'Insa memorja',
      'Ipproponi għarfien tal-kumpanija',
      'Ipproponi bidla fil-paġna tal-għarfien',
      'Issejvja riżultat',
    ],
    [
      'Isem',
      'Titlu',
      'Deskrizzjoni',
      'Istruzzjonijiet',
      'Eżempju',
      'Test',
      'Kontenut',
      'Dikjarazzjoni',
      'Ambitu',
      'Tema',
      'Lingwa',
      'Żona tal-ħin',
      'Mudell taċ-chat',
      'Mudell',
      'Fajls',
      'Sorsi',
      'Kontenut',
      'Tip',
      'Jissostitwixxi',
      'Reviżjoni bażi',
      'Azzjoni',
      'Kategorija',
      'Kontenut',
      'Raġuni',
      'Evidenza',
    ],
  ),
  ro: other(
    [
      'Aprobă',
      'Respinge',
      'Deschide',
      'Poți spune și „aprobă”.',
      'Aprobat prin mesajul tău',
      'În așteptare',
      'În execuție',
      'Aprobat',
      'Respins',
      'Eșuat',
      'Expirat',
      'Înlocuit',
      'Examinează cererea',
      'Conținut',
    ],
    [
      'Creează schiță Skill',
      'Actualizează schiță Skill',
      'Creează schiță agent',
      'Schimbă setările',
      'Salvează memoria',
      'Uitǎ memoria',
      'Propune cunoștințe ale companiei',
      'Propune modificare pagină',
      'Salvează rezultatul',
    ],
    [
      'Nume',
      'Titlu',
      'Descriere',
      'Instrucțiuni',
      'Exemplu',
      'Text',
      'Conținut',
      'Afirmație',
      'Domeniu',
      'Temă',
      'Limbă',
      'Fus orar',
      'Model chat',
      'Model',
      'Fișiere',
      'Surse',
      'Conținut',
      'Tip',
      'Înlocuiește',
      'Revizie de bază',
      'Acțiune',
      'Categorie',
      'Conținut',
      'Motiv',
      'Dovezi',
    ],
  ),
  sk: other(
    [
      'Schváliť',
      'Odmietnuť',
      'Otvoriť',
      'Môžete tiež povedať „schváliť“.',
      'Schválené vašou správou',
      'Čaká',
      'Prebieha',
      'Schválené',
      'Zamietnuté',
      'Zlyhalo',
      'Platnosť vypršala',
      'Nahradené',
      'Skontrolovať žiadosť',
      'Obsah',
    ],
    [
      'Vytvoriť koncept Skill',
      'Aktualizovať koncept Skill',
      'Vytvoriť koncept agenta',
      'Zmeniť nastavenia',
      'Uložiť pamäť',
      'Zabudnúť pamäť',
      'Navrhnúť firemné znalosti',
      'Navrhnúť zmenu znalostnej stránky',
      'Uložiť výsledok',
    ],
    [
      'Názov',
      'Titulok',
      'Popis',
      'Pokyny',
      'Príklad',
      'Text',
      'Obsah',
      'Tvrdenie',
      'Rozsah',
      'Téma',
      'Jazyk',
      'Časové pásmo',
      'Chatovací model',
      'Model',
      'Súbory',
      'Zdroje',
      'Obsah',
      'Typ',
      'Nahrádza',
      'Základná revízia',
      'Akcia',
      'Kategória',
      'Obsah',
      'Dôvod',
      'Dôkazy',
    ],
  ),
  sl: other(
    [
      'Odobri',
      'Zavrni',
      'Odpri',
      'Lahko tudi rečeš »odobri«.',
      'Odobreno s tvojim sporočilom',
      'Čaka',
      'V izvajanju',
      'Odobreno',
      'Zavrnjeno',
      'Neuspešno',
      'Poteklo',
      'Zamenjano',
      'Preglej zahtevo',
      'Vsebina',
    ],
    [
      'Ustvari osnutek Skilla',
      'Posodobi osnutek Skilla',
      'Ustvari osnutek agenta',
      'Spremeni nastavitve',
      'Shrani spomin',
      'Pozabi spomin',
      'Predlagaj znanje podjetja',
      'Predlagaj spremembo strani znanja',
      'Shrani rezultat',
    ],
    [
      'Ime',
      'Naslov',
      'Opis',
      'Navodila',
      'Primer',
      'Besedilo',
      'Vsebina',
      'Trditev',
      'Obseg',
      'Tema',
      'Jezik',
      'Časovni pas',
      'Klepetalni model',
      'Model',
      'Datoteke',
      'Viri',
      'Vsebina',
      'Vrsta',
      'Zamenja',
      'Osnovna revizija',
      'Dejanje',
      'Kategorija',
      'Vsebina',
      'Razlog',
      'Dokazi',
    ],
  ),
  da: other(
    [
      'Godkend',
      'Afvis',
      'Åbn',
      'Du kan også skrive ”godkend”.',
      'Godkendt af din besked',
      'Afventer',
      'Kører',
      'Godkendt',
      'Afvist',
      'Mislykket',
      'Udløbet',
      'Erstattet',
      'Gennemgå anmodning',
      'Indhold',
    ],
    [
      'Opret Skill-kladde',
      'Opdater Skill-kladde',
      'Opret agentkladde',
      'Skift indstillinger',
      'Gem hukommelse',
      'Glem hukommelse',
      'Foreslå virksomhedsviden',
      'Foreslå videnssideændring',
      'Gem arbejdsresultat',
    ],
    [
      'Navn',
      'Titel',
      'Beskrivelse',
      'Instruktioner',
      'Eksempel',
      'Tekst',
      'Indhold',
      'Udsagn',
      'Omfang',
      'Tema',
      'Sprog',
      'Tidszone',
      'Chatmodel',
      'Model',
      'Filer',
      'Kilder',
      'Indhold',
      'Type',
      'Erstatter',
      'Basisrevision',
      'Handling',
      'Kategori',
      'Indhold',
      'Årsag',
      'Dokumentation',
    ],
  ),
  de: other(
    [
      'Genehmigen',
      'Ablehnen',
      'Öffnen',
      'Du kannst auch „genehmigen“ schreiben.',
      'Durch deine Nachricht genehmigt',
      'Ausstehend',
      'Wird ausgeführt',
      'Genehmigt',
      'Abgelehnt',
      'Fehlgeschlagen',
      'Abgelaufen',
      'Ersetzt',
      'Anfrage prüfen',
      'Inhalt',
    ],
    [
      'Skill-Entwurf erstellen',
      'Skill-Entwurf aktualisieren',
      'Agentenentwurf erstellen',
      'Einstellungen ändern',
      'Erinnerung speichern',
      'Erinnerung vergessen',
      'Unternehmenswissen vorschlagen',
      'Wissensseite vorschlagen',
      'Arbeitsergebnis speichern',
    ],
    [
      'Name',
      'Titel',
      'Beschreibung',
      'Anweisungen',
      'Beispiel',
      'Text',
      'Inhalt',
      'Aussage',
      'Bereich',
      'Thema',
      'Sprache',
      'Zeitzone',
      'Chatmodell',
      'Modell',
      'Dateien',
      'Quellen',
      'Inhalt',
      'Typ',
      'Ersetzt',
      'Basisrevision',
      'Aktion',
      'Kategorie',
      'Inhalt',
      'Grund',
      'Belege',
    ],
  ),
  es: other(
    [
      'Aprobar',
      'Rechazar',
      'Abrir',
      'También puedes decir «aprobar».',
      'Aprobado por tu mensaje',
      'Pendiente',
      'En curso',
      'Aprobado',
      'Rechazado',
      'Fallido',
      'Caducado',
      'Sustituido',
      'Revisar solicitud',
      'Contenido',
    ],
    [
      'Crear borrador de Skill',
      'Actualizar borrador de Skill',
      'Crear borrador de agente',
      'Cambiar configuración',
      'Guardar memoria',
      'Olvidar memoria',
      'Sugerir conocimiento de empresa',
      'Sugerir cambio de página',
      'Guardar resultado',
    ],
    [
      'Nombre',
      'Título',
      'Descripción',
      'Instrucciones',
      'Ejemplo',
      'Texto',
      'Contenido',
      'Declaración',
      'Ámbito',
      'Tema',
      'Idioma',
      'Zona horaria',
      'Modelo de chat',
      'Modelo',
      'Archivos',
      'Fuentes',
      'Contenido',
      'Tipo',
      'Sustituye',
      'Revisión base',
      'Acción',
      'Categoría',
      'Contenido',
      'Motivo',
      'Evidencia',
    ],
  ),
  fr: other(
    [
      'Approuver',
      'Refuser',
      'Ouvrir',
      'Vous pouvez aussi dire « approuver ».',
      'Approuvé par votre message',
      'En attente',
      'En cours',
      'Approuvé',
      'Refusé',
      'Échec',
      'Expiré',
      'Remplacé',
      'Examiner la demande',
      'Contenu',
    ],
    [
      'Créer un brouillon de Skill',
      'Mettre à jour le brouillon de Skill',
      'Créer un brouillon d’agent',
      'Modifier les réglages',
      'Enregistrer un souvenir',
      'Oublier un souvenir',
      'Suggérer une connaissance d’entreprise',
      'Suggérer une modification de page',
      'Enregistrer un résultat',
    ],
    [
      'Nom',
      'Titre',
      'Description',
      'Instructions',
      'Exemple',
      'Texte',
      'Contenu',
      'Énoncé',
      'Portée',
      'Thème',
      'Langue',
      'Fuseau horaire',
      'Modèle de chat',
      'Modèle',
      'Fichiers',
      'Sources',
      'Contenu',
      'Type',
      'Remplace',
      'Révision de base',
      'Action',
      'Catégorie',
      'Contenu',
      'Motif',
      'Preuve',
    ],
  ),
  it: other(
    [
      'Approva',
      'Rifiuta',
      'Apri',
      'Puoi anche dire «approva».',
      'Approvato dal tuo messaggio',
      'In attesa',
      'In esecuzione',
      'Approvato',
      'Rifiutato',
      'Fallito',
      'Scaduto',
      'Sostituito',
      'Esamina richiesta',
      'Contenuto',
    ],
    [
      'Crea bozza Skill',
      'Aggiorna bozza Skill',
      'Crea bozza agente',
      'Modifica impostazioni',
      'Salva memoria',
      'Dimentica memoria',
      'Suggerisci conoscenza aziendale',
      'Suggerisci modifica pagina',
      'Salva risultato',
    ],
    [
      'Nome',
      'Titolo',
      'Descrizione',
      'Istruzioni',
      'Esempio',
      'Testo',
      'Contenuto',
      'Dichiarazione',
      'Ambito',
      'Tema',
      'Lingua',
      'Fuso orario',
      'Modello chat',
      'Modello',
      'File',
      'Fonti',
      'Contenuto',
      'Tipo',
      'Sostituisce',
      'Revisione base',
      'Azione',
      'Categoria',
      'Contenuto',
      'Motivo',
      'Prova',
    ],
  ),
  nl: other(
    [
      'Goedkeuren',
      'Afwijzen',
      'Openen',
      'Je kunt ook “goedkeuren” zeggen.',
      'Goedgekeurd via je bericht',
      'In afwachting',
      'Wordt uitgevoerd',
      'Goedgekeurd',
      'Afgewezen',
      'Mislukt',
      'Verlopen',
      'Vervangen',
      'Verzoek bekijken',
      'Inhoud',
    ],
    [
      'Skillconcept maken',
      'Skillconcept bijwerken',
      'Agentconcept maken',
      'Instellingen wijzigen',
      'Geheugen opslaan',
      'Geheugen vergeten',
      'Bedrijfskennis voorstellen',
      'Kenniswijziging voorstellen',
      'Werkresultaat opslaan',
    ],
    [
      'Naam',
      'Titel',
      'Beschrijving',
      'Instructies',
      'Voorbeeld',
      'Tekst',
      'Inhoud',
      'Stelling',
      'Bereik',
      'Thema',
      'Taal',
      'Tijdzone',
      'Chatmodel',
      'Model',
      'Bestanden',
      'Bronnen',
      'Inhoud',
      'Type',
      'Vervangt',
      'Basisrevisie',
      'Actie',
      'Categorie',
      'Inhoud',
      'Reden',
      'Bewijs',
    ],
  ),
  pl: other(
    [
      'Zatwierdź',
      'Odrzuć',
      'Otwórz',
      'Możesz też napisać „zatwierdź”.',
      'Zatwierdzone przez Twoją wiadomość',
      'Oczekuje',
      'W toku',
      'Zatwierdzone',
      'Odrzucone',
      'Nieudane',
      'Wygasłe',
      'Zastąpione',
      'Przejrzyj prośbę',
      'Treść',
    ],
    [
      'Utwórz szkic Skill',
      'Zaktualizuj szkic Skill',
      'Utwórz szkic agenta',
      'Zmień ustawienia',
      'Zapisz pamięć',
      'Zapomnij pamięć',
      'Zaproponuj wiedzę firmy',
      'Zaproponuj zmianę strony',
      'Zapisz wynik',
    ],
    [
      'Nazwa',
      'Tytuł',
      'Opis',
      'Instrukcje',
      'Przykład',
      'Tekst',
      'Treść',
      'Stwierdzenie',
      'Zakres',
      'Motyw',
      'Język',
      'Strefa czasowa',
      'Model czatu',
      'Model',
      'Pliki',
      'Źródła',
      'Treść',
      'Typ',
      'Zastępuje',
      'Wersja bazowa',
      'Działanie',
      'Kategoria',
      'Treść',
      'Powód',
      'Dowody',
    ],
  ),
  pt: other(
    [
      'Aprovar',
      'Rejeitar',
      'Abrir',
      'Também pode dizer «aprovar».',
      'Aprovado pela sua mensagem',
      'Pendente',
      'Em execução',
      'Aprovado',
      'Rejeitado',
      'Falhou',
      'Expirado',
      'Substituído',
      'Rever pedido',
      'Conteúdo',
    ],
    [
      'Criar rascunho de Skill',
      'Atualizar rascunho de Skill',
      'Criar rascunho de agente',
      'Alterar definições',
      'Guardar memória',
      'Esquecer memória',
      'Sugerir conhecimento da empresa',
      'Sugerir alteração de página',
      'Guardar resultado',
    ],
    [
      'Nome',
      'Título',
      'Descrição',
      'Instruções',
      'Exemplo',
      'Texto',
      'Conteúdo',
      'Afirmação',
      'Âmbito',
      'Idioma',
      'Idioma',
      'Fuso horário',
      'Modelo de chat',
      'Modelo',
      'Ficheiros',
      'Fontes',
      'Conteúdo',
      'Tipo',
      'Substitui',
      'Revisão base',
      'Ação',
      'Categoria',
      'Conteúdo',
      'Motivo',
      'Evidência',
    ],
  ),
  sv,
};
const REQUIRED_LOCALES = [
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
];
if (REQUIRED_LOCALES.some((locale) => !ACTION_COPY[locale]))
  throw new Error('missing action locale');
export function actionCopy(locale: string): ActionCopy {
  const copy = ACTION_COPY[locale.split('-')[0]] ?? ACTION_COPY.en;
  const notes =
    memberNotesCopy[locale.split('-')[0] as keyof typeof memberNotesCopy] ?? memberNotesCopy.en;
  return {
    ...copy,
    types: {
      ...copy.types,
      ...SETTINGS_ACTION_TYPES,
      ...appActionTypes(locale),
      add_meeting_note: notes.add,
      propose_knowledge_change: copy.types.suggest_page_edits,
    },
    fields: { ...copy.fields, ...SETTINGS_ACTION_FIELDS, ...personalSettingsFields(locale) },
    toolFields: appToolFields(locale),
    words: namespaceCopyFor(appApprovalsCopy, locale),
  };
}
